// history-fold.ts — pure replay of a persisted session transcript
// (HistoryEntry[]) into the { messages, toolResults, systemEvents } shape the
// MessageFeed renderer consumes. Used by the read-only shared-chat viewer
// (/shared/[owner]/[id]), which has no live WS to drive ws-client's reactive
// fold.
//
// This mirrors the turn-folding rules in ws-client.svelte.ts (applyServerMsg /
// applyEntry) but is a PURE, non-reactive subset: no analytics tracker, no
// listeners, no askPending/liveness clock, and no live optimistic states. Every
// turn it emits is already SETTLED (stopReason set) — the transcript is finished
// history, so the feed never shows a streaming caret or running pill. The
// reliability-critical live client stays untouched.

import {
	appendContextCompactionBoundary,
	appendResponseTextIfMissing,
	contextCompactedFeedMessage,
	discardPartialAssistantRows,
	responseTextAlreadyRenderedInTurn,
	responseUsageToTokenUsage,
	upsertToolUse,
} from './events.ts';
import type { ContentBlock, ContextCompactedMessage, Message, SystemEvent, ToolResult, ToolUse } from './events.ts';
import type { HistoryEntry, InlineImage, ServerMsg } from './agent-protocol.ts';
import type { TransparencySkillEvent } from './transparency-summary.ts';

export interface UserMessage {
	role: 'user';
	content: string;
	images?: InlineImage[];
	timestamp: number;
	clientMsgId: string;
	delivery: 'delivered';
}

export type FeedMessage = Message | UserMessage | ContextCompactedMessage;

export interface FoldedHistory {
	messages: FeedMessage[];
	toolResults: Map<string, ToolResult>;
	systemEvents: SystemEvent[];
	skillEvents: TransparencySkillEvent[];
}

export function foldHistory(entries: HistoryEntry[]): FoldedHistory {
	const messages: FeedMessage[] = [];
	const toolResults = new Map<string, ToolResult>();
	const systemEvents: SystemEvent[] = [];
	const skillEvents: TransparencySkillEvent[] = [];

	let openAssistant: Message | null = null;
	// The first assistant row belongs to the active engine turn even if an
	// interleaved other-tab user_echo closes `openAssistant` to queue a follow-up.
	let turnFirstAssistant: Message | null = null;
	// Claude can close a streamed row at a synchronous compaction boundary immediately
	// before its terminal response. Keep that row long enough to attach duplicate
	// terminal fallback text/usage without opening a false row after the divider.
	let assistantClosedByCompaction: Message | null = null;

	function startTurnIfNeeded(at: number): Message {
		if (!openAssistant) {
			const fresh: Message = { role: 'assistant', content: [], timestamp: at };
			messages.push(fresh);
			openAssistant = fresh;
			turnFirstAssistant ??= fresh;
		}
		return openAssistant;
	}

	function closeTurn(usage?: Message['usage'], stopReason: string = 'end_turn'): void {
		if (openAssistant) {
			if (usage) openAssistant.usage = usage;
			openAssistant.stopReason = stopReason;
		}
		openAssistant = null;
	}

	function pushUser(content: string, clientMsgId: string, at: number, images?: InlineImage[]): void {
		closeTurn();
		assistantClosedByCompaction = null;
		const msg: UserMessage = { role: 'user', content, timestamp: at, clientMsgId, delivery: 'delivered' };
		if (images && images.length > 0) msg.images = images;
		messages.push(msg);
	}

	function applyServerMsg(m: ServerMsg, at: number): void {
		switch (m.type) {
			case 'context_compacted': {
				const assistantAtBoundary = openAssistant;
				openAssistant = appendContextCompactionBoundary(
					messages,
					openAssistant,
					contextCompactedFeedMessage(m, at),
					turnFirstAssistant,
				);
				assistantClosedByCompaction =
					assistantAtBoundary && openAssistant !== assistantAtBoundary
						? assistantAtBoundary
						: null;
				return;
			}
			case 'discard_partial': {
				// The compaction divider is durable history, but every assistant row
				// from the killed attempt is partial and must be replaced by the re-run.
				discardPartialAssistantRows(messages, m.clientMsgId);
				openAssistant = null;
				turnFirstAssistant = null;
				assistantClosedByCompaction = null;
				return;
			}
			case 'user_echo': {
				// In a finished transcript the user_message client entry already
				// pushed the bubble; only insert if it's missing (defensive).
				const existing = m.clientMsgId
					? messages.find(
							(x): x is UserMessage => x.role === 'user' && x.clientMsgId === m.clientMsgId,
						)
					: undefined;
				if (existing) {
					if (m.images && m.images.length > 0 && !existing.images) existing.images = m.images;
				} else {
					pushUser(m.content, m.clientMsgId ?? crypto.randomUUID(), at, m.images);
				}
				return;
			}
			case 'thought': {
				startTurnIfNeeded(at).content.push({ type: 'text', text: m.text });
				return;
			}
			case 'thinking': {
				startTurnIfNeeded(at).content.push({ type: 'thinking', thinking: m.text, startedAt: at });
				return;
			}
			case 'text_start': {
				const turn = startTurnIfNeeded(at);
				turn.content.push(
					m.kind === 'thinking'
						? { type: 'thinking', thinking: '', startedAt: at }
						: { type: 'text', text: '' },
				);
				return;
			}
			case 'text_delta': {
				const turn = startTurnIfNeeded(at);
				const last: ContentBlock | undefined = turn.content[turn.content.length - 1];
				if (last && last.type === 'text') last.text += m.text;
				else if (last && last.type === 'thinking') last.thinking += m.text;
				else turn.content.push({ type: 'text', text: m.text });
				return;
			}
			case 'action': {
				const turn = startTurnIfNeeded(at);
				const block: ToolUse = {
					type: 'tool_use',
					id: m.id,
					name: m.tool,
					input: (m.input ?? {}) as Record<string, unknown>,
					// Reload parity with the live fold: stamp the per-tool spawn time so a
					// reopened chat's dock (and any timer) anchors honestly.
					startedAt: at,
				};
				// Same upsert rule as the live fold: a re-emitted action with the same
				// id (Codex web_search) replaces the block instead of duplicating it.
				upsertToolUse(turn.content, block);
				return;
			}
			case 'tool_result': {
				toolResults.set(m.toolUseId, {
					type: 'tool_result',
					toolUseId: m.toolUseId,
					content: m.preview,
					isError: !m.ok,
				});
				return;
			}
			case 'response': {
				let terminalAssistant = openAssistant;
				if (m.text.trim()) {
					if (responseTextAlreadyRenderedInTurn(messages, m.text, m.clientMsgId)) {
						terminalAssistant ??= assistantClosedByCompaction ?? turnFirstAssistant;
					} else {
						terminalAssistant ??= startTurnIfNeeded(at);
						appendResponseTextIfMissing(terminalAssistant, m.text);
					}
				} else {
					terminalAssistant ??= assistantClosedByCompaction ?? turnFirstAssistant;
				}
				// Carry ALL usage fields incl. cacheRead/cacheCreation (the bulk of a long
				// chat's context the composer meter measures) — mirrors ws-client's live
				// fold; both once hardcoded the cache fields to 0 and undercounted ~70×.
				const usage = responseUsageToTokenUsage(m.usage);
				if (terminalAssistant === openAssistant) closeTurn(usage);
				else if (terminalAssistant) terminalAssistant.usage = usage;
				assistantClosedByCompaction = null;
				turnFirstAssistant = null;
				return;
			}
			case 'error': {
				closeTurn();
				assistantClosedByCompaction = null;
				turnFirstAssistant = null;
				// Carry usage-limit metadata on reload too, so a chat reopened after a
				// limit hit still offers the switch (parity with the live path).
				systemEvents.push({
					subtype: 'bridge_error',
					message: m.message,
					code: m.code,
					provider: m.provider,
					clientMsgId: m.clientMsgId,
				});
				return;
			}
			case 'stopped': {
				// Mirror the live fold: a distinct stopReason lets the Turn Summary card
				// read "Stopped after N steps" on shared/replayed views too.
				closeTurn(undefined, 'stopped');
				assistantClosedByCompaction = null;
				turnFirstAssistant = null;
				return;
			}
			case 'skill_used': {
				skillEvents.push({ type: 'used', skillId: m.skillId, createdAt: at });
				return;
			}
			case 'skill_created': {
				skillEvents.push({
					type: 'changed',
					capabilityId: m.capabilityId,
					name: m.name,
					outcome: m.outcome ?? 'created',
					createdAt: at,
					...(m.targetId ? { targetId: m.targetId } : {}),
				});
				return;
			}
			// ping / session_ready / session_name / ask carry no
			// transcript content — nothing to render in a static history view.
			default:
				return;
		}
	}

	for (const entry of entries) {
		if (entry.dir === 'client') {
			const msg = entry.msg;
			if (msg.type === 'user_message') {
				pushUser(msg.content, msg.clientMsgId ?? crypto.randomUUID(), entry.at, msg.images);
			}
			// 'stop' / 'ask_answer' client entries don't render.
		} else {
			applyServerMsg(entry.msg as ServerMsg, entry.at);
		}
	}

	// The transcript is finished — settle any still-open trailing turn so the feed
	// renders it as done (no streaming caret / animated thinking block).
	closeTurn();
	assistantClosedByCompaction = null;
	turnFirstAssistant = null;

	return { messages, toolResults, systemEvents, skillEvents };
}
